import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { Place } from '@elara/client-core/models/PlaceModel';

import { useEffect, useRef } from 'react';

import { getPlaceIcon } from '@elara/client-core/lib/place-category';
import { cn, formatAddress, pluralize } from '@elara/client-core/lib/utils';

import { sentry } from '#lib/sentry.js';
import { formatDisplayUrl, formatTelHref } from '#lib/url-utils.js';
import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useChatInputState, usePlaceGuideState, useTripsState } from '#states/hooks.js';
import { ItineraryButton } from '#ui';
import { Alert, AlertDescription, AlertTitle } from '#ui/alert.js';
import { AppImage } from '#ui/AppImage.js';
import { AskElaraButton } from '#ui/AskElaraButton.js';
import { Button } from '#ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '#ui/card.js';
import { MapWidget } from '#ui/chat/index.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { ElaraIcon } from '#ui/icons/elara-icon.js';
import Loader from '#ui/loader.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { ShortlistButton } from '#ui/shortlist-button.js';
import { StarRating } from '#ui/StarRating.js';

import { GuideLayout } from './GuideLayout.js';
import { GuideNotFound } from './GuideNotFound.js';
import { PlaceAmenities } from './PlaceAmenities.js';
import { PlanTripCTA } from './PlanTripCTA.js';

import { OpeningHoursTable } from '../OpeningHoursTable.js';

/** Renders a single sub-destination as a compact clickable row. */
function SubDestinationItem({ placeId, places }: { placeId: string; places: PlacesContainer }) {
  const tripsState = useTripsState();
  useRefreshPlace(placeId);
  const handle = places.ClientPlace.get({ id: placeId });

  if (!handle.isReady) {
    return <div className="h-10 animate-pulse bg-muted rounded" />;
  }

  const sub = handle.data as Place | undefined;
  if (!sub) return null;

  return (
    <div
      className={cn(
        'group',
        'flex flex-shrink-0 items-center gap-3 w-full text-left p-2 -mx-2 rounded-md hover:bg-muted transition-colors cursor-pointer @md:w-40 overflow-hidden',
        '@md:flex-col @md:items-start @md:gap-2 @md:p-0 @md:mx-0 @md:hover:bg-transparent',
      )}
      onClick={() => tripsState.pushGuide('place', placeId)}
    >
      {sub.photos?.[0]?.url ? (
        <AppImage
          src={sub.photos[0].url}
          alt=""
          className="w-10 h-10 @md:h-40 @md:w-40 rounded object-cover flex-shrink-0 sepia-0 contrast-100 group-hover:sepia-80 group-hover:contrast-90"
        />
      ) : (
        <div className="w-10 h-10 @md:h-40 @md:w-40 rounded bg-muted flex items-center justify-center flex-shrink-0">
          <MaterialIcon icon="place" size={IconSizes.sm} className="text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1 @md:w-full @md:overflow-hidden">
        <div className="font-medium text-sm truncate">{sub.displayName}</div>
        {sub.primaryCategory && (
          <div className="text-xs text-muted-foreground capitalize truncate">{sub.primaryCategory}</div>
        )}
      </div>
    </div>
  );
}

interface PlaceGuideProps {
  placeId?: string;
}
export function PlaceGuide({ placeId }: PlaceGuideProps) {
  const placeGuideState = usePlaceGuideState();
  const chatInputState = useChatInputState();
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const { places, flows } = features as {
    places: PlacesContainer;
    flows: Record<string, (...args: any[]) => Promise<unknown>>;
  };
  const enrichTriggeredRef = useRef(new Set<string>());

  useRefreshPlace(placeId);

  useEffect(() => {
    placeGuideState.reset();

    if (placeId) {
      placeGuideState.findPlace(placeId);
    }
    // placeGuideState is a stable reference from DI container, no need to include in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  const place: Place | undefined = placeGuideState.place;

  // Trigger AI enrichment only when the place needs it
  useEffect(() => {
    if (!place || !placeId || !place.needsEnrichment || enrichTriggeredRef.current.has(placeId)) return;
    enrichTriggeredRef.current.add(placeId);
    const enrichPlace = flows.enrichPlace as ((input: { placeId: string }) => Promise<unknown>) | undefined;
    if (!enrichPlace) return;
    void enrichPlace({ placeId }).catch((error: unknown) => {
      enrichTriggeredRef.current.delete(placeId);
      sentry?.captureException(error);
    });
  }, [place, placeId, place?.needsEnrichment, flows]);

  // A place that is also a known hotel opens the hotel guide instead — replacing this guide, so Back
  // goes to wherever the place was opened from rather than into a guide that redirects straight back.
  const hotelId = place?.ids?.hotelId;
  useEffect(() => {
    if (hotelId) tripsState.replaceGuide('hotel', hotelId);
  }, [hotelId, tripsState]);

  tripsState.setPreviewPanelTitle(place?.displayName ?? '');

  if (!placeId) {
    return <GuideNotFound guide="place" title="Place not found" />;
  }

  if (placeGuideState.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  if (!place) {
    return <GuideNotFound guide="place" title="Place not found" />;
  }

  // Redirecting to the hotel guide (see the effect above).
  if (hotelId) return null;

  const mapPoints = place.mapPoint ? [place.mapPoint] : [];

  const generatedDescription = place.generated?.description;
  const generatedAdvisory = place.generated?.advisory;

  return (
    <GuideLayout
      images={place.allImages}
      title={place.displayName}
      metadata={
        <>
          {place.primaryCategory && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon={getPlaceIcon(place.type)} size={IconSizes.xs} />
              <span className="capitalize">{place.primaryCategory}</span>
            </div>
          )}
          {place.rating != null && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="star" size={IconSizes.xs} />
              <span>{place.rating}</span>
            </div>
          )}
          {place.priceRangeLabel && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="payments" size={IconSizes.xs} />
              <span>{place.priceRangeLabel}</span>
            </div>
          )}
          {place.shortAddress && (
            <div className="flex items-center gap-1">
              <MaterialIcon icon="location_on" size={IconSizes.xs} />
              <span className="truncate">{place.shortAddress}</span>
            </div>
          )}
        </>
      }
      actions={
        tripsState.current ? (
          <>
            <AskElaraButton
              variant="icon"
              onClick={() => {
                const id = place.id ?? placeId;
                if (id) {
                  chatInputState.addDataAttachment('place', id, place.displayName);
                  tripsState.switchToPanel('chat');
                  tripsState.closePreviewPanel();
                  chatInputState.focusInput();
                }
              }}
            />
            <ShortlistButton itemType="place" referenceId={place.id ?? placeId} title={place.displayName} />
            <ItineraryButton
              itemType="place"
              referenceId={place.id ?? placeId}
              title={place.displayName}
              requireDateConfirmation={true}
            />
          </>
        ) : (
          // Opened outside a trip (e.g. from a Story): the actions above all need a current trip.
          <PlanTripCTA type="place" id={place.id ?? placeId} name={place.displayName} />
        )
      }
    >
      <>
        {place.googleBusinessStatus === 'CLOSED_TEMPORARILY' && (
          <Alert variant="destructive" className="flex items-center gap-2">
            <MaterialIcon icon="error" size={IconSizes.sm} />
            <AlertDescription>
              {place.displayName} is <span className="font-semibold">temporarily closed</span> and may not be available
              for visits.
            </AlertDescription>
          </Alert>
        )}

        {/* Long description and map */}
        <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8">
          {generatedDescription ? (
            <div className="flex flex-col items-start gap-4">
              {generatedAdvisory && (
                <Alert className="flex flex-col gap-2 w-full max-w-prose">
                  <AlertTitle className="flex items-center gap-2">
                    <ElaraIcon />
                    <span className="font-medium">Elara tips</span>
                  </AlertTitle>
                  <AlertDescription>{generatedAdvisory}</AlertDescription>
                </Alert>
              )}
              <SimpleMarkdown
                text={generatedDescription}
                className="text-pretty max-w-prose [&>p:first-child]:text-lg [&>p:first-child]:leading-[145%]"
              />
            </div>
          ) : (
            <div className="space-y-2 max-w-prose">
              <div className="h-6 w-full animate-pulse bg-muted rounded" />
              <div className="h-6 w-full animate-pulse bg-muted rounded" />
              <div className="h-6 w-3/4 animate-pulse bg-muted rounded" />
            </div>
          )}
          <div>
            <MapWidget key={placeId} points={mapPoints} zoom={16} size="full" className="min-h-96" />
          </div>
        </div>

        <hr />

        <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8 items-start">
          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-[16px_auto] gap-2 items-start">
                {place.contactLinks?.website && (
                  <>
                    <div className="flex items-center gap-2 mt-1">
                      <MaterialIcon icon="public" size={IconSizes.xs} />
                    </div>
                    <a
                      href={place.contactLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link truncate"
                    >
                      {formatDisplayUrl(place.contactLinks.website)}
                    </a>
                  </>
                )}
                {place.contactLinks?.phone && (
                  <>
                    <div className="flex items-center gap-2 mt-1">
                      <MaterialIcon icon="call" size={IconSizes.xs} />
                    </div>
                    <a
                      href={formatTelHref(place.contactLinks.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                    >
                      {place.contactLinks.phone}
                    </a>
                  </>
                )}
                {place.address && (
                  <>
                    <div className="flex items-center gap-2 mt-1">
                      <MaterialIcon icon="location_on" size={IconSizes.xs} />
                    </div>
                    <div>
                      <p className="whitespace-pre-wrap">{formatAddress(place.address, '\n')}</p>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          if (place.googleMapsLinks?.directionsUri) {
                            window.open(place.googleMapsLinks.directionsUri, '_blank');
                          }
                        }}
                      >
                        Get directions ↗
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {place.openingHours && (
            <Card>
              <CardHeader>
                <CardTitle>Opening hours</CardTitle>
              </CardHeader>
              <CardContent>
                {place.openingHours.periods && place.openingHours.periods.length > 0 ? (
                  <OpeningHoursTable
                    openingHours={place.openingHours}
                    orderByToday={true}
                    highlightToday={true}
                    isOpenNow={place.isOpenNow}
                  />
                ) : place.openingHours.weekdayText && place.openingHours.weekdayText.length > 0 ? (
                  <p className="text-sm whitespace-pre-wrap">{place.openingHours.weekdayText.join('\n')}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">No opening hours available.</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {place.reviewSummary && (
          <>
            <hr />
            <Card className="@3xl:col-span-2">
              <CardHeader>
                <CardTitle>Reviews</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {place.rating && <StarRating rating={place.rating} showValue={true} size="md" />}
                <div className="text-pretty max-w-prose">{place.reviewSummary}</div>
                {place.userRatingsTotal && (
                  <div className="text-sm text-muted-foreground">
                    Based on{' '}
                    {place.googleMapsLinks?.reviewsUri ? (
                      <a href={place.googleMapsLinks?.reviewsUri} target="_blank" className="link">
                        {place.userRatingsTotal} Google {pluralize(place.userRatingsTotal, 'review')} ↗
                      </a>
                    ) : (
                      <span>
                        {place.userRatingsTotal} Google {pluralize(place.userRatingsTotal, 'review')}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <PlaceAmenities place={place} />

        {place.relatedPlaceIds && place.relatedPlaceIds.length > 0 && (
          <>
            <hr />
            <Card>
              <CardHeader>
                <CardTitle>Places you’ll find here</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col mt-2 gap-1 @md:flex-row @md:flex-wrap @md:gap-4 w-full">
                  {place.relatedPlaceIds.map((subId: string) => (
                    <SubDestinationItem key={subId} placeId={subId} places={places} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </>
    </GuideLayout>
  );
}
